import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useApi from "../../hooks/useApi";

import { normalizeString } from "../../utils/helpers";

import CustomButton from "../../Components/CustomButton";
import Input from "../../Components/Input";
import Loader from "../../Components/Loader";
import RenderActivities from "../../Components/RenderActivities";

import "./style.scss";

const FindProvider = () => {
  const [allServices, setAllServices] = useState([]);
  const [category, setCategory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [topSuggestions, setTopSuggestions] = useState([]);
  const [filtredSuggestions, setFilterSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const searchRef = useRef(null);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { get, loading } = useApi();

  // Fetch suggestions from API
  const fetchSuggestions = async () => {
    try {
      const data = await get("/Providers/Services");

      let allActivities = data.activites.flatMap((item) => item.subCategories);

      let allCategory = data.activites.flatMap((item) => item.category);

      setCategory(allCategory);

      setAllServices(allActivities);
      setTopSuggestions(data.topActivites);
    } catch (error) {
      // Fallback to mock data on error
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filterSuggestions = (query) => {
    let value = query.target.value;
    setSearchQuery(value);

    if (true) {
      let filteredSug = allServices.filter((item) =>
        t(item.toLowerCase()).toLowerCase().includes(value.toLowerCase()),
      );

      setFilterSuggestions(filteredSug || []);
      setShowSuggestions(true);
    }
  };

  const handleSearch = (query) => {
    const searchTerm = query || searchQuery;

    if (searchTerm.trim()) {
      navigate(`/search-results?activity=${searchTerm.trim().toLowerCase()}`);
      setShowSuggestions(false);
    }
  };

  const handleTagClick = (tag) => {
    navigate(`/search-results?activity=${tag.toLowerCase()}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleInputFocus = (e) => {
    filterSuggestions(e);
    if (filtredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/search-results?category=${category.toLowerCase()}`);
  };

  const handleSearchButtinDisable = () => {
    if (filterSuggestions) {
      let isActiv = filtredSuggestions.map((item) => {
        return item.toLowerCase() === searchQuery;
      });

      if (isActiv[0]) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  return (
    <section className="hero-section">
      {/* Animated background elements */}
      <div className="animated-bg"></div>
      <div className="animated-circle circle-1"></div>
      <div
        className="animated-circle circle-2"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="animated-circle circle-3"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="content-wrapper">
        <div className="container">
          <div className="content-grid">
            {/* Left Side - Consumer Search */}
            <div className="search-section">
              <div className="heading">
                <h1>
                  {t("find_your_perfect")}
                  <span>{t("coach_or_instructor")}</span>
                </h1>
                <h2 className="text-2xl text-white font-bold pb-2">
                  {t("browse_by_category")}
                </h2>
              </div>

              <div className="category-list">
                {category &&
                  category.map((item) => {
                    return (
                      <div
                        className="single-category"
                        key={item}
                        onClick={() => handleCategoryClick(item)}
                      >
                        <RenderActivities activities={item} />
                        {t(normalizeString(item))}
                      </div>
                    );
                  })}
              </div>

              {/* Search Bar */}
              <div className="search-bar">
                <div className="search-inner">
                  <div className="search-input-wrapper">
                    <div className="search-input" ref={searchRef}>
                      {/* <Search className="search-icon" /> */}
                      <Input
                        placeholder={t("search_placeholder")}
                        value={searchQuery}
                        onChange={filterSuggestions}
                        onKeyPress={handleKeyPress}
                        onFocus={handleInputFocus}
                        label={t("search_for_coaches")}
                      />

                      {/* Suggestions Dropdown */}
                      {showSuggestions && filtredSuggestions.length > 0 && (
                        <div className="suggestions-dropdown">
                          {filtredSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              className="suggestion-item"
                              onClick={() => {
                                handleTagClick(suggestion);
                              }}
                            >
                              <div className="suggestion-content">
                                <span>{t(suggestion.toLowerCase())}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <CustomButton
                      className="search-button"
                      onClick={() => handleSearch()}
                      disabled={handleSearchButtinDisable()}
                    >
                      {t("search")}
                    </CustomButton>
                  </div>
                  <div className="top-tags">
                    {topSuggestions &&
                      topSuggestions.map((tag) => (
                        <span
                          key={tag}
                          className="tag"
                          onClick={() => handleTagClick(tag)}
                        >
                          {t(tag)}
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {loading && <Loader />}
    </section>
  );
};

export default FindProvider;
